import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Label } from "@tellescope/design-system";
import { Plus } from "lucide-react";
import type { VisibilityCondition } from "@tellescope/visibility-schema";
import { ProductListItem } from "./ProductListItem";
import { ProductPickerDialog } from "./ProductPickerDialog";
import type { SelectedShopEntry } from "./types";
import type { StripeShopProduct, StripeShopPrice } from "./stripeShopTypes";
import type { useShopBlockEditorLogic } from "./useShopBlockEditorLogic";

interface ProductListSectionProps {
  logic: ReturnType<typeof useShopBlockEditorLogic>;
}

/**
 * Unified "Products" section: the reorderable list of already-added products (each row is its own
 * configuration surface — price variants, category, badge, recommended, visibility), plus a
 * "+ Add Product" dialog that's the only place the Stripe catalog is browsed/searched.
 */
export const ProductListSection = ({ logic }: ProductListSectionProps) => {
  const { t } = useTranslation("shop");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);

  const [expandedProductId, setExpandedProductIdState] = useState<string | null>(null);

  const setExpandedProductId = (productId: string | null) => {
    setExpandedProductIdState(productId);
    logic.setDrillDownProductId(productId);
  };

  const handleToggleExpanded = (stripeProductId: string) => {
    setExpandedProductId(expandedProductId === stripeProductId ? null : stripeProductId);
  };

  // The picker dialog shares the single drillDownProductId-keyed price fetch with this list, so a
  // row left expanded behind the dialog would otherwise start showing whatever product the dialog
  // is browsing. Collapsing the row whenever the dialog opens sidesteps that entirely — there's
  // nothing behind the dialog to bleed into.
  const openPicker = () => {
    setExpandedProductId(null);
    setPickerOpen(true);
  };

  const handleDragStart = (stripeProductId: string) => {
    setExpandedProductId(null);
    setDraggedProductId(stripeProductId);
  };

  const handleDragOver = (e: React.DragEvent, targetStripeProductId: string) => {
    e.preventDefault();
    if (!draggedProductId || draggedProductId === targetStripeProductId) return;

    const fromIndex = logic.selection.findIndex((entry) => entry.stripeProductId === draggedProductId);
    const toIndex = logic.selection.findIndex((entry) => entry.stripeProductId === targetStripeProductId);
    if (fromIndex === -1 || toIndex === -1) return;

    logic.reorderSelection(fromIndex, toIndex);
  };

  const handleDragEnd = () => setDraggedProductId(null);

  const isVariantResolved = (stripePriceId: string) =>
    // Adding/removing ANY price re-keys the whole reverse-lookup query, so gating on one aggregate
    // "has the current fetch settled" flag would suppress every row's answer whenever a sibling row
    // changes — including rows whose answer was already known, which flickered their warning off and
    // back on instead of the newly-added row's. hasCheckedPriceId answers per price ID from an
    // accumulator that persists known answers across query-key changes, so only a genuinely new,
    // never-before-checked price ID defers to "assume resolved" here.
    !logic.hasCheckedPriceId(stripePriceId) || logic.productByStripePriceId.has(stripePriceId);

  const handleTogglePrice = (product: StripeShopProduct, price: StripeShopPrice) => {
    logic.togglePrice(product, price);
  };

  const handleVisibilityChange = (stripeProductId: string, condition: VisibilityCondition | undefined) => {
    logic.setEntryVisibility(stripeProductId, condition);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{t("Products")}</Label>
        <Button variant="ghost" size="sm" onClick={openPicker}>
          <Plus className="h-4 w-4 mr-1" />
          {t("Add Product")}
        </Button>
      </div>

      {logic.selection.length > 0 ? (
        <div className="space-y-1.5">
          {logic.selection.map((entry: SelectedShopEntry) => (
            <ProductListItem
              key={entry.stripeProductId}
              entry={entry}
              isVariantResolved={isVariantResolved}
              isDragging={draggedProductId === entry.stripeProductId}
              onDragStart={() => handleDragStart(entry.stripeProductId)}
              onDragOver={(e) => handleDragOver(e, entry.stripeProductId)}
              onDragEnd={handleDragEnd}
              isExpanded={expandedProductId === entry.stripeProductId}
              onToggleExpanded={() => handleToggleExpanded(entry.stripeProductId)}
              onRemove={() => logic.removeSelection(entry.stripeProductId)}
              prices={expandedProductId === entry.stripeProductId ? logic.pricesQuery.prices : []}
              pricesLoading={expandedProductId === entry.stripeProductId && logic.pricesQuery.isLoading}
              onTogglePrice={handleTogglePrice}
              onBadgeChange={(badge) => logic.setEntryBadge(entry.stripeProductId, badge)}
              onRecommendedChange={(recommended) => logic.setEntryRecommended(entry.stripeProductId, recommended)}
              quantitySupport={logic.getQuantitySupport(entry)}
              onMaxQuantityChange={(inputValue) => logic.setEntryMaxQuantity(entry.stripeProductId, inputValue)}
              onCategoryChange={(category) => logic.setEntryCategory(entry.stripeProductId, category)}
              onVisibilityChange={(condition) => handleVisibilityChange(entry.stripeProductId, condition)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground mb-2">{t("No products yet")}</p>
          <Button variant="outline" size="sm" onClick={openPicker}>
            <Plus className="h-4 w-4 mr-1" />
            {t("Add First Product")}
          </Button>
        </div>
      )}

      <ProductPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        accounts={logic.accounts}
        accountsLoading={logic.accountsLoading}
        stripeKey={logic.stripeKey}
        onStripeKeyChange={logic.setStripeKey}
        selectedProductCount={logic.selection.length}
        productsQuery={logic.productsQuery}
        pricesQuery={logic.pricesQuery}
        onDrillDownProductChange={logic.setDrillDownProductId}
        isPriceSelected={logic.isPriceSelected}
        onTogglePrice={handleTogglePrice}
      />
    </div>
  );
};
